/**
 * @file EnterpriseInquiriesPage.tsx
 * @brief Super Admin → Enterprise inquiries from the mobile Plans form.
 *
 * @details
 * Lists contact-sales leads (seats, name, email, phone, notes). Status can be
 * moved new → contacted → closed. After close, provision via Companies →
 * Enterprise (ADR-020).
 *
 * @author DES
 * @date 26-07-2026
 * @updated 01-10-2026: the status filter became a header pill
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/data/DataTable";
import { Button } from "@/components/ui/Button";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import type { SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { formatDateTime } from "@/shared/utils/formatters";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { cn } from "@/shared/theme/cn";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import {
  useEnterpriseInquiriesList,
  useUpdateEnterpriseInquiryStatus,
} from "./api";
import type {
  EnterpriseInquiry,
  EnterpriseInquiryStatus,
} from "./schemas";

const PAGE_SIZE = 25;
const ALL = "__all__";

const STATUS_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "All statuses" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "closed", label: "Closed" },
];

/** Dot colour for the header status pill. */
const INQUIRY_STATUS_DOT: Record<string, string> = {
  new: "bg-brand",
  contacted: "bg-warning",
  closed: "bg-success",
};

/** The bare field's value line, for a read-only value (17px medium, as
 *  `bareControlClass`), so the detail reads like the forms around it. */
const readOnlyValueClass = "min-h-6 text-[17px] font-medium leading-[21px] text-app-text";

function InquiryDetailDialog({
  inquiry,
  onOpenChange,
  onStatusChange,
}: {
  inquiry: EnterpriseInquiry | null;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (status: EnterpriseInquiryStatus) => void;
}) {
  const updateStatus = useUpdateEnterpriseInquiryStatus();
  if (!inquiry) return null;

  const companyHref = `/admin/companies?q=${encodeURIComponent(inquiry.company_name || "")}`;

  return (
    <FormSheet open={!!inquiry} onOpenChange={onOpenChange}>
      {/* DES 01-10-2026: form standard. Cmd+K palette frame; read-only
          values as bare fields in three columns; the pipeline stage is the
          round status pill beside the title. */}
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <div className="flex items-start justify-between gap-6">
              <div className="min-w-0 flex-1 space-y-1.5">
                <FormSheetTitle>Enterprise inquiry</FormSheetTitle>
                {/* Submitted moved here, so the six contact values fill two
                    rows of three instead of leaving it alone on a third. */}
                <FormSheetDescription>
                  Contact-sales lead from the mobile Plans form, submitted{" "}
                  {formatDateTime(inquiry.created_at)}.
                </FormSheetDescription>
              </div>
              {/* mr-10 clears the sheet's close ✕ in the corner. */}
              <div className="mr-10 flex shrink-0 flex-col items-end gap-1">
                <Select
                  value={inquiry.status}
                  onValueChange={(v) => {
                    const status = v as EnterpriseInquiryStatus;
                    updateStatus.mutate(
                      { id: inquiry.id, status },
                      { onSuccess: () => onStatusChange(status) },
                    );
                  }}
                  disabled={updateStatus.isPending}
                >
                  <SelectTrigger
                    aria-label="Pipeline stage"
                    className="!h-9 !w-auto gap-2 !rounded-full !px-4 text-sm font-semibold focus:!ring-0 focus:!ring-offset-0 focus-visible:bg-foreground/5 focus-visible:outline-none"
                  >
                    {/* A div: the trigger line-clamps its direct spans. */}
                    <div className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className={cn(
                          "block h-2 w-2 shrink-0 rounded-full",
                          INQUIRY_STATUS_DOT[inquiry.status] ?? "bg-muted-foreground",
                        )}
                      />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </FormSheetHeader>

          <FormSheetBody>
            <FormSheetSection title="Contact">
              <FormSheetFields className="sm:grid-cols-3">
                <FormSheetField>
                  <FormFieldGroup label="Type">
                    <p className={readOnlyValueClass}>
                      {inquiry.intent === "change_plan" ? "Change / end plan" : "New deal"}
                    </p>
                  </FormFieldGroup>
                </FormSheetField>
                <FormSheetField>
                  <FormFieldGroup label="Name">
                    <p className={cn(readOnlyValueClass, "truncate")}>{inquiry.contact_name}</p>
                  </FormFieldGroup>
                </FormSheetField>
                <FormSheetField>
                  <FormFieldGroup label="Seats">
                    <p className={cn(readOnlyValueClass, "tabular-nums")}>
                      {inquiry.seats_required}
                    </p>
                  </FormFieldGroup>
                </FormSheetField>
                <FormSheetField>
                  <FormFieldGroup label="Email">
                    <a
                      className={cn(readOnlyValueClass, "block truncate text-brand hover:underline")}
                      href={`mailto:${inquiry.contact_email}`}
                    >
                      {inquiry.contact_email}
                    </a>
                  </FormFieldGroup>
                </FormSheetField>
                <FormSheetField>
                  <FormFieldGroup label="Phone">
                    <a
                      className={cn(readOnlyValueClass, "block truncate text-brand hover:underline")}
                      href={`tel:${inquiry.contact_phone}`}
                    >
                      {inquiry.contact_phone}
                    </a>
                  </FormFieldGroup>
                </FormSheetField>
                <FormSheetField>
                  {/* The company link stays here; the separate outline "Open
                      company" button it duplicated is gone (buttons are red
                      or grey, never outline). */}
                  <FormFieldGroup label="Company">
                    <p className={cn(readOnlyValueClass, "truncate")}>
                      {inquiry.company_id ? (
                        <Link to={companyHref} className="text-brand hover:underline">
                          {inquiry.company_name || "Company"}
                        </Link>
                      ) : (
                        inquiry.company_name || "Not given"
                      )}
                    </p>
                  </FormFieldGroup>
                </FormSheetField>
              </FormSheetFields>
            </FormSheetSection>

            <FormSheetSection title="To discuss">
              <FormSheetFields className="sm:grid-cols-1">
                <FormSheetField span={2}>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    {inquiry.message?.trim() || "No message."}
                  </p>
                </FormSheetField>
              </FormSheetFields>
            </FormSheetSection>
          </FormSheetBody>

          <FormSheetFooter>
            <FormSheetFooterCell>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className={formSheetActionClass}
              >
                Close
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}

export function EnterpriseInquiriesPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 350);
  const [selected, setSelected] = useState<EnterpriseInquiry | null>(null);
  const updateStatus = useUpdateEnterpriseInquiryStatus();

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
    status:
      statusFilter === ALL
        ? ("" as const)
        : (statusFilter as EnterpriseInquiryStatus),
  };

  const { data, isLoading, isFetching, error, refetch } =
    useEnterpriseInquiriesList(params);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<ColumnDef<EnterpriseInquiry>[]>(
    () => [
      {
        header: "When",
        accessorKey: "created_at",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
      {
        header: "Contact",
        accessorKey: "contact_name",
        cell: ({ row }) => (
          <div className="min-w-[160px]">
            <p className="font-medium">{row.original.contact_name}</p>
            <p className="text-xs text-muted-foreground">
              {row.original.contact_email}
            </p>
          </div>
        ),
      },
      {
        header: "Phone",
        accessorKey: "contact_phone",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.contact_phone}</span>
        ),
      },
      {
        header: "Type",
        accessorKey: "intent",
        cell: ({ row }) => (
          <span
            className={
              row.original.intent === "change_plan"
                ? "rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-100"
                : "rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground"
            }
          >
            {row.original.intent === "change_plan" ? "Change / end" : "New deal"}
          </span>
        ),
      },
      {
        header: "Seats",
        accessorKey: "seats_required",
        cell: ({ row }) => (
          <span className="font-medium tabular-nums">
            {row.original.seats_required}
          </span>
        ),
      },
      {
        header: "Company",
        accessorKey: "company_name",
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.company_name || "—"}
          </span>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <Select
            value={row.original.status}
            onValueChange={(v) => {
              updateStatus.mutate({
                id: row.original.id,
                status: v as EnterpriseInquiryStatus,
              });
            }}
            disabled={updateStatus.isPending}
          >
            <SelectTrigger
              className="h-8 w-[120px]"
              onClick={(e) => e.stopPropagation()}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        ),
      },
    ],
    [updateStatus],
  );

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">
              Enterprise inquiries
            </h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Leads from the mobile Plans → Enterprise form. Superadmins are also
              emailed on each submission. After sales close, provision seats via
              Companies → Enterprise.
            </p>
          </div>
          {/* The filter, top right (DES 01-10-2026). */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <HeaderFilterMenu
              title="Filter by status"
              searchPlaceholder="Search statuses"
              items={STATUS_ITEMS}
              selected={statusFilter}
              label={
                STATUS_ITEMS.find((i) => i.value === statusFilter)?.label ??
                STATUS_ITEMS[0].label
              }
              active={statusFilter !== ALL}
              onClear={() => {
                setStatusFilter(ALL);
                setPage(1);
              }}
              onSelect={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            />
          </div>
        </GridBand>

        {/* The search moved to the bottom dock (DES 01-10-2026). */}
        <GridCells className="grid-cols-1">
          <GridCell padded={false}>
            <div className="flex h-11 items-center justify-between gap-3 px-4">
              <span className="text-xs text-muted-foreground">{total} total</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
                disabled={isFetching}
              >
                Refresh
              </Button>
            </div>
          </GridCell>
        </GridCells>

        <GridCell padded={false} className="border-b-0">
          <DataTable
            variant="cell"
            columns={columns}
            data={rows}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
            onRetry={() => void refetch()}
            emptyTitle="No inquiries yet"
            emptyDescription="When someone submits the Enterprise form on Plans, it will show up here."
            rowKey={(row) => row.id}
            onRowClick={(row) => setSelected(row)}
            pagination={{
              page,
              limit: PAGE_SIZE,
              total,
              totalPages,
            }}
            onPageChange={setPage}
          />
        </GridCell>
      </GridBlock>

      {/* The Notes screen's search (DES 01-10-2026), docked at the bottom
          like Notes. */}
      <PageDock>
        <ExpandingSearch
          value={searchInput}
          onChange={(next) => {
            setSearchInput(next);
            setPage(1);
          }}
          placeholder="Search name, email, company"
          label="Search inquiries"
        />
      </PageDock>

      <InquiryDetailDialog
        inquiry={selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        onStatusChange={(status) => {
          setSelected((prev) => (prev ? { ...prev, status } : null));
        }}
      />
    </div>
  );
}
